"use client";

import React, { useState, useMemo, useRef, useEffect } from "react";
import Input from "@/components/ui/input";
import PrimaryButton from "@/components/ui/primary-button";
import { FiSearch } from "react-icons/fi";
import Fuse, { IFuseOptions } from "fuse.js";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export interface DropdownSearchBarProps<T> {
  items: T[];
  initialSearchTerm?: string;
  onSearch?: (term: string) => void;
  onSearchTermChange?: (term: string) => void;
  onResultClick?: (result: T) => void;
  placeholder?: string;
  fuseOptions?: IFuseOptions<T>;
  renderResult?: (result: T) => React.ReactNode;
}

const DropdownSearchBar = <T,>({
  items = [],
  initialSearchTerm = "",
  onSearch,
  onSearchTermChange,
  onResultClick,
  placeholder = "Search here",
  fuseOptions,
  renderResult = (result: T) => <>{JSON.stringify(result)}</>,
}: DropdownSearchBarProps<T>) => {
  const [showSuggestions, setShowSuggestions] = useState(
    initialSearchTerm !== ""
  );
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [isShiftDown, setIsShiftDown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Use memoization to optimize Fuse instance creation
  const fuse = useMemo(() => {
    const defaultFuseOptions: IFuseOptions<T> = {
      threshold: 0.3,
      includeScore: true,
    };

    const options = { ...defaultFuseOptions, ...fuseOptions };
    return new Fuse<T>(items, options);
  }, [items, fuseOptions]);

  // Memoize filtered results to prevent unnecessary recalculations
  const filteredResults = useMemo(() => {
    if (!fuse || !searchTerm) return [];

    try {
      return fuse.search(searchTerm);
    } catch (err) {
      toast.error(`Error searching: "${searchTerm}"`);
      console.error("Error searching:", err);
      return [];
    }
  }, [fuse, searchTerm]);

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchTerm);
    }
  };

  const handleResultClick = (result: T) => {
    if (onResultClick) {
      onResultClick(result);
    }
    setSearchTerm("");
    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Shift") {
      setIsShiftDown(true);
    }
    if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < filteredResults.length) {
        handleResultClick(filteredResults[activeIndex].item);
      } else {
        handleSearch();
      }
    } else if (e.key === "Escape") {
      setSearchTerm("");
      setShowSuggestions(false);
      setActiveIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex < filteredResults.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
    } else if (e.key === "Tab") {
      if (filteredResults.length <= 0) {
        return;
      }

      e.preventDefault();
      if (activeIndex < 0 && filteredResults.length > 0) {
        setActiveIndex(0);
      } else if (activeIndex >= 0 && activeIndex < filteredResults.length) {
        if (isShiftDown) {
          setActiveIndex(
            (activeIndex - 1 + filteredResults.length) % filteredResults.length
          );
        } else {
          setActiveIndex((activeIndex + 1) % filteredResults.length);
        }
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Shift") {
      setIsShiftDown(false);
    }
  };

  useEffect(() => {
    if (!resultsRef.current || activeIndex < 0) return;

    const activeElement = resultsRef.current.children[
      activeIndex
    ] as HTMLElement;
    if (activeElement) {
      activeElement.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // Extracted handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSearchTermChange) {
      onSearchTermChange(e.target.value);
    }
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setActiveIndex(-1);
  };

  const handleInputFocus = () => setShowSuggestions(true);

  const handleInputBlur = () =>
    setTimeout(() => setShowSuggestions(false), 200);

  const handleResultKeyDown =
    (result: T) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        handleResultClick(result);
      }
    };

  return (
    <div className="relative">
      <div className="flex h-10 items-center gap-2">
        <Input
          ref={inputRef}
          type="text"
          variant="border"
          placeholder={placeholder}
          value={searchTerm}
          error={false}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          baseInputClassName="md:py-3 py-2"
          className="flex-grow rounded-2xl font-roboto"
        />
        <PrimaryButton
          className="flex w-fit items-center"
          size="md"
          color="orange"
          onClick={handleSearch}
        >
          <FiSearch />
          <span className="hidden font-montserrat text-white md:block">
            Search
          </span>
        </PrimaryButton>
      </div>
      {showSuggestions && filteredResults.length > 0 && (
        <div
          ref={resultsRef}
          className="absolute left-0 z-50 mt-1 max-h-60 overflow-clip overflow-y-auto rounded-lg bg-white shadow-[2px_8px_24px_0px_#03030326]"
          style={{
            width: inputRef.current ? inputRef.current.offsetWidth : "auto",
          }}
        >
          {filteredResults.map((result, index) => (
            <div
              key={index}
              className={cn(
                "cursor-pointer gap-2 px-4 py-2 font-roboto text-body-1-400 ease-out",
                "focus:border focus:border-[#9E9691] active:border-[#9E9691]",
                index === activeIndex
                  ? "border border-[#9E9691] bg-neutral-100"
                  : "bg-white hover:bg-neutral-100"
              )}
              onClick={() => handleResultClick(result.item)}
              onKeyDown={handleResultKeyDown(result.item)}
            >
              {renderResult(result.item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownSearchBar;
